<!-- eslint-disable max-len -->
<template>
  <div
    class="main-product critical-component-clear"
    :class="{ [$class('loading')]: state === 'loading' }"
  >
    <div class="grid">
      <div class="col xs-span l9 main-product__content">
        <product-gallery
          class="main-product__gallery"
          :current-index="currentIndex"
          :current-namespace="currentNamespace"
          layout="horizontal"
          :product="product || liquid"
          :variant="variant"
          @update-current-index="handleUpdateCurrentIndex"
          @update-current-namespace="handleUpdateCurrentNamespace"
        />

        <div
          v-if="hasBadge && currentCountry == 'US' || currentCountry == 'CA'"
          class="main-product__badges"
        >
          <product-badge
            v-if="sale"
            :text="$string('collection.badge.sale')"
            :badge-class="productBadgeClass($string('collection.badge.sale'))"
          />
          <template
            v-for="(badge, index) of badges"
            :key="`product-badge-${index}`"
          >
            <product-badge
              :text="productBadgeText(badge)"
              :badge-class="productBadgeClass(badge)"
            />
          </template>
        </div>
      </div>

      <div
        ref="sidebarWrapper"
        class="main-product__sidebar-wrapper col xs-span l3"
      >
        <div
          ref="sidebar"
          class="main-product__sidebar"
        >
          <main-product-sidebar-details
            :liquid="liquid"
            :product="product"
            :variant="variant"
            @scroll-to-view="scrollToReview"
          />

          <product-form
            v-if="!isSawmill(product.type) || !isTransactional || !product.bundles && !product.attachments"
            ref="productForm"
            class="main-product__form"
            :product="product || liquid"
            :variant="variant"
            :quantity-selector="false"
            :cartbtn="cartbtn"
            :btntext="btntext"
            :btnurl="btnurl"
            @update-variant="handleVariantUpdate"
          />

          <btn
            v-if="(isTransactional && isSawmill(product.type)) && product.bundles && product.attachments"
            class="main-product__drawer-toggle"
            modifiers="fill block center"
            :label="$string('product.sidebar.select')"
            hover-only
            @click="handleDrawerClick"
          >
            <template #hoverIcon>
              <icon-cart />
            </template>
          </btn>

          <btn
            v-if="!isTransactional && dealer"
            class="main-product__drawer-toggle"
            modifiers="fill block center"
            :label="$string('product.form.dealer')"
            hover-only
            @click="navigateToDealer"
          >
            <template #hoverIcon>
              <icon-cart />
            </template>
          </btn>

          <main-product-sidebar-usp
            :blocks="blocks"
            :product="product || liquid"
            :varaint=variant
            @variant-usp="handleUspUpdate"
          />
          <main-product-sidebar-contact
            :contact="contact"
          />
        </div>
      </div>

      <div
        v-if="isTransactional && showStickyMobileAtc"
        class="main-product__sticky-atc-wrapper"
      >
        <btn
          class="main-product__add-to-cart"
          :label="mobileAddToCartText"
          modifiers="fill block center"
          :show-state-icon="state === 'adding'"
          hover-only
          @click="handleMobileAtcClick"
        >
          <template #hoverIcon>
            <icon-cart />
          </template>
        </btn>
      </div>

      <overlay
        v-if="(isTransactional && isSawmill(product.type)) && product.bundles && product.attachments"
        key="productPurchaseDrawer"
        block-class="product-form-drawer"
        direction="left"
        namespace="productPurchaseDrawer"
        type="drawer"
        :template="false"
      >
        <template #body="overlay">
          <product-form-drawer
            :product="product || liquid"
            :overlay="overlay"
            :icon="drawericon"
          />
        </template>
      </overlay>
    </div>
    <div
      v-if="isTransactional && showStickyMobileAtc"
      class="main-product__mobile-atc"
    >
      <btn
        class="product-form__add-to-cart"
        modifiers="fill block center"
        :show-state-icon="state === 'adding'"
        hover-only
        type="submit"
        @click.prevent="triggerAddToCart"
      >
        <template #hoverIcon>
          <icon-cart />
        </template>
      </btn>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Main product
 * -----------------------------------------------------------------------------
 * Main product page component.
 *
 * @param {Object} liquid - Formatted product Liquid object.
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 *
 * @emits updateRequestObject - Emit new product object.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'
import ProductForm from '~async/product-form/product-form'
import ProductFormDrawer from '~async/product-form-drawer/product-form-drawer'
import ProductGallery from '~async/product-gallery/product-gallery'

import ProductBadge from '~async/product-badge/product-badge.vue'
import MainProductSidebarUsp from './main-product-sidebar-usp'
import MainProductSidebarContact from './main-product-sidebar-contact'
import MainProductSidebarDetails from './main-product-sidebar-details'
import Overlay from '~global/overlay/overlay'
import IconCart from '~icons/button/cart.svg'
import Btn from '~global/btn/btn'

import productQuery from '~graphql/product.gql'
import { formatProduct } from '~/helpers/format'
import { emitUpdateRequestObject } from '~/helpers/vue'
import { debounce } from '~/helpers/general'

export default {
  name: 'MainProduct',

  components: {
    ProductBadge,
    ProductForm,
    ProductFormDrawer,
    ProductGallery,
    MainProductSidebarUsp,
    MainProductSidebarContact,
    MainProductSidebarDetails,
    Overlay,
    Btn,
    IconCart,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        description: '',
        price: 0,
        title: '',
      }),
    },
    labels: {
      type: Object,
      default: () => ({
        new: '',
        sale: '',
        most_popular: '',
        best_seller: '',
        freeShipping: '',
      }),
    },
    contact: {
      type: Object,
      default: () => ({
        title: '',
        subCopy: '',
        phone: '',
        buttonText: '',
        buttonUrl: '',
      }),
    },
    product: {
      type: [Boolean, Object],
      default: false,
    },
    variant: {
      type: [Boolean, Object],
      default: false,
    },
    drawericon : {
      type: [String],
      default: false,
    },
    blocks: {
      type: [Array],
      default: () => {
        return []
      },
    },
    dealer: {
      type: [Boolean, String],
      default: false,
    },
    cartbtn : {
      type: Boolean,
      default: false,
    },
    btntext : {
      type: [String],
      default: false,
    },
    btnurl : {
      type: [String],
      default: false,
    },
  },

  emits: {
    'update-request-object': emitUpdateRequestObject,
  },

  data() {
    return {
      badges: [],
      currentIndex: 0,
      currentNamespace: 'gallery',
      handle: cnvs.page.handle,
      state: 'loading',
      isTransactional: false,
      sale: null,
      showStickyMobileAtc: false,
      currentCountry: `${cnvs.store.country}`,
    }
  },
  computed: {
    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getFirstElement: 'accessibility/getFirstElement',
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Compute mobile add to cart sticky button text
     */
    mobileAddToCartText() {
      if (this.isSawmill(this.product.type)) {
        return this.$string('product.sidebar.select')
      }

      return this.$string('product.cta.add_to_cart')
    },

    hasBadge() {
      if (!this.product && !this.liquid) {
        return
      }

      return this.sale || this.badges.length >= 0
    },
  },
 
  watch: {

    /**
     * Watch handle for update to load new product.
     */
    handle() {
      this.queryProduct(this.handle)
    },
  },

  mounted() {
    if (window.cnvs.transactionalMarkets.includes(window.cnvs.store.country.toLowerCase())) {
      this.isTransactional = true
    }
    this.setProduct()
    this.bindEventListeners()
    this.footer = document.querySelector('.site-footer-group-section')

    this.setSidebarWidth()

    if (this.isInView(this.footer, 'top')) {
      this.$refs.sidebar.classList.add('no-overflow')
    }

    if (this.isInView(this.$refs.sidebar)) {
      this.stickySidebar()
    }

    if (this.isInView(this.$refs.sidebarWrapper, 'bottom-above')) {
      this.stickyMobileAtc()
    }
    console.log('product-------------',product)
  },


  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
    }),

    /**
     * Check if element container is in view
     *
     * @param {Element} container - element container
     * @param {String} position - check if top, bottom, bottom-above
     * or both of screen
     *
     */
    isInView(container, position) {
      const windowHeight = window.innerHeight
      const elementRectangle = container.getBoundingClientRect()
      const elementTop = elementRectangle.top
      const elementBottom = elementRectangle.bottom
      const topInView = (elementTop > 0) && (elementTop < windowHeight)
      const bottomInView = (elementBottom > 0) && (elementBottom < windowHeight)
      const bottomIsAbove = elementBottom < 0
      if (position === 'top' && topInView) {
        return true
      }

      if (position === 'bottom' && bottomInView) {
        return true
      }

      if (position === 'both' && topInView && bottomInView) {
        return true
      }

      if (position === 'bottom-above' && bottomIsAbove) {
        return true
      }

      return false
    },

    /**
     * Set width of sidebar when sidebar is sticky
     */
    setSidebarWidth() {
      const wrapperWidth = this.$refs.sidebarWrapper.offsetWidth
      this.$refs.sidebar.style.width = `${wrapperWidth}px`
    },

    /**
     * Lock sidebar
     */
    stickySidebar() {
      this.$refs.sidebar.classList.add('is-sticky')
    },

    /**
     * Unlock sidebar
     */
    unStickySidebar() {
      this.$refs.sidebar.classList.remove('is-sticky')
      this.$refs.sidebar.style.transform = `translateY(0px)`
    },

    /**
     * Show sticky add to cart button on mobile
     */
    stickyMobileAtc() {
      if (this.isMobile) {
        this.showStickyMobileAtc = true
        return
      }

      this.showStickyMobileAtc = false
    },

    /**
     * Hide sticky add to cart button on mobile
     */
    unStickyMobileAtc() {
      this.showStickyMobileAtc = false
    },

    /**
     * CSS transform sidebar position when footer comes into view
     */
    transformSidebar(footer) {
      const footerPosition = footer.getBoundingClientRect()
      const windowHeight = window.innerHeight

      this.$refs.sidebar.style.transform = `translateY(${footerPosition.top - windowHeight}px)`
    },

    /**
     * Bind Event listeners
     */
    bindEventListeners() {

      /**
       * Add default listeners.
       */
      window.visualViewport.addEventListener(
        'resize',
        debounce(() => this.setSidebarWidth()),
        { passive: true },
      )

      document.addEventListener('scroll', () => {
        const position = window.scrollY

        if (this.isInView(this.$refs.sidebar, 'bottom')) {
          this.stickySidebar()
        }

        if (this.isInView(this.$refs.sidebarWrapper, 'bottom-above')) {
          this.stickyMobileAtc()
        }

        if (this.isInView(this.$refs.sidebarWrapper, 'bottom')) {
          this.unStickyMobileAtc()
        }

        if (position === 0) {
          this.unStickySidebar()
          this.unStickyMobileAtc()
        }

        if (this.isInView(this.footer, 'top')) {
          this.transformSidebar(this.footer)
        }
      })
    },

    /**
     * Set Liquid product object to product.
     */
    setProduct() {
      if (!this.liquid.handle) {
        this.queryProduct()
        return
      }

      this.$emit('update-request-object', {
        response: this.liquid,
        type: 'product',
      })

      this.state = 'ready'

      this.getBadges()
    },

    /**
     * Loads product from GraphQL.
     * @param {String} [handle] - Product handle to load.
     */
    async queryProduct(handle = this.handle) {
      if (handle === false || this.product.handle === handle) {
        return
      }

      this.state = 'loading'

      try {
        const response = await cnvs.Query({
          query: productQuery,
          variables: {
            country: cnvs.store.country,
            handle,
            language: cnvs.store.language,
          },
        })

        if (cnvs.settings.disableComponentReadyState) {
          return
        }

        this.$emit('update-request-object', {
          response: formatProduct(response),
          type: 'product',
        })

        this.state = 'ready'
        this.getBadges()

      } catch (error) {
        cnvs.ReportError('Failed to load product', error)
      }
    },

    /**
     * Update current carousel index.
     * @param {Number} newIndex - New carousel index.
     */
    handleUpdateCurrentIndex(newIndex) {
      if (newIndex === this.currentIndex) {
        return
      }

      this.currentIndex = newIndex
    },

    /**
     * Update current carousel namespace.
     * @param {String} newNamespace - New carousel namespace.
     */
    handleUpdateCurrentNamespace(newNamespace) {
      this.currentNamespace = newNamespace
    },

    /**
     * Handle variant update from product form.
     * @param {Object} variant - Selected variant.
     */
    handleVariantUpdate(variant) {
      this.$emit('update-request-object', {
        response: variant,
        type: 'variant',
      })

      const params = new URLSearchParams(document.location.search.substring(1))

      if (variant?.id) {
        params.set('variant', variant.id)
      }

      const url = variant?.id
        ? `${location.origin}${location.pathname}?${params.toString()}`
        : `${location.origin}${location.pathname}`

      history.replaceState({
        html: '',
      }, '', url)
    },

    /**
     * Handle zoom click.
     */
    handleDrawerClick(event) {
      event.preventDefault()

      this.openOverlay({
        ignoreDismissed: true,
        namespace: 'productPurchaseDrawer',
      })
    },

    /**
     * Handle mobile add to cart click
     * @param {Event} event - Click event
     */
    handleMobileAtcClick(event) {
      if (this.isSawmill(this.product.type) &&
        this.product.bundles &&
        this.product.attachments) {
        this.handleDrawerClick(event)
      }

      if (!this.isSawmill(this.product.type) ||
        (!this.product.bundles &&
          !this.product.attachments)) {
        cnvs.EventBus.emit('add-to-cart')
      }
    },
    scrollToReview() {
      const reviews = document.querySelector('[id^="ReviewsWidget"]')

      if (!reviews) {
        return
      }

      reviews.scrollIntoView({ behavior: 'smooth' })
    },

    getBadges() {
      if (!this.product && !this.liquid) {
        return
      }

      const product = this.product || this.liquid
      const badges = product.tags?.filter((tag) => tag.includes('badge:'))
      const sale = product.compareAtPrice.amount
        ? product.price?.amount < product.compareAtPrice?.amount
        : false

      if ((!badges || badges.length <= 0) && !sale) {
        return
      }
      const badgeTitle = badges.map((badge) => badge.replace('badge:', ''))

      this.badges = badgeTitle
      this.sale = sale
    },

    /**
     * Generate badge class to get proper badge color
     * @param {String} badge - badge name.
     */
    productBadgeClass(badge) {
      const text = badge.replace('_', '-').replace(' ', '-')
      return `product-badge--${text.toLowerCase()}`
    },

    /**
     * Generate badge text label by checking if it exists as a setting
     * @param {String} badge - badge name.
     */
    productBadgeText(badge) {
      if (badge.includes('_') && this.labels[`${badge}`]) {
        return this.labels[`${badge}`]
      }

      return badge
    },

    isSawmill(type) {
      if (!type) {
        return
      }

      if (type.toLowerCase() === 'sawmill') {
        return true
      }
    },

    triggerAddToCart() {
      this.$refs.productForm.handleAddToCart()
    },

    navigateToDealer() {
      if (!this.dealer) {
        return
      }

      window.open(this.dealer, '_blank')
    },
  },
}
</script>

<style lang="scss">
@import '~async/product-gallery-modal/product-gallery-modal-overlay';
@import './main-product';
@import '@/components/reviews-io';
</style>
